"use client";

import { useState, type CSSProperties } from "react";
import { HStack } from "@astryxdesign/core/HStack";
import { Text } from "@astryxdesign/core/Text";
import { Link } from "@astryxdesign/core/Link";
import { VStack } from "@astryxdesign/core/VStack";
import { YandexIcon } from "@/components/auth/yandex-icon";
import { VkIcon } from "@/components/auth/vk-icon";
import { yandexSignInAction } from "@/app/(auth)/actions";
import { captureAttributionBeforeOAuth } from "@/lib/attribution/capture-client";
import { CONSENT_LINKS } from "@/lib/consent/constants";
import { useResolvedDarkMode } from "@/lib/theme/use-resolved-dark";

type OAuthSignInButtonsProps = {
  yandexEnabled: boolean;
  vkEnabled: boolean;
  yandexLabel?: string;
  vkLabel?: string;
};

function YandexSignInButton({ label }: { label: string }) {
  const isDark = useResolvedDarkMode();
  const [pending, setPending] = useState(false);

  async function handleClick() {
    if (pending) return;
    setPending(true);
    await captureAttributionBeforeOAuth("yandex");
    await yandexSignInAction();
  }

  return (
    <button
      type="button"
      onClick={() => void handleClick()}
      disabled={pending}
      className="flex h-11 w-full cursor-pointer items-center justify-center gap-2.5 rounded-[var(--radius-element)] px-4 text-[15px] font-bold leading-none transition-opacity hover:opacity-90 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 disabled:cursor-wait disabled:opacity-70"
      style={
        {
          backgroundColor: isDark ? "#ffffff" : "#000000",
          color: isDark ? "#000000" : "#ffffff",
          outlineColor: isDark ? "#ffffff" : "#000000",
        } satisfies CSSProperties
      }
    >
      <YandexIcon className="shrink-0" />
      <span>{label}</span>
    </button>
  );
}

function VkSignInButton({ label }: { label: string }) {
  const [pending, setPending] = useState(false);

  async function handleClick() {
    if (pending) return;
    setPending(true);
    await captureAttributionBeforeOAuth("vk");
    window.location.assign("/api/auth/vk");
  }

  return (
    <button
      type="button"
      onClick={() => void handleClick()}
      disabled={pending}
      className="flex h-11 w-full cursor-pointer items-center justify-center gap-2.5 rounded-[var(--radius-element)] bg-[#0077FF] px-4 text-[15px] font-bold leading-none text-white transition-opacity hover:opacity-90 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#0077FF] disabled:cursor-wait disabled:opacity-70"
    >
      <VkIcon className="shrink-0" />
      <span>{label}</span>
    </button>
  );
}

export function OAuthSignInButtons({
  yandexEnabled,
  vkEnabled,
  yandexLabel = "Войти с Яндекс ID",
  vkLabel = "Войти с VK ID",
}: OAuthSignInButtonsProps) {
  if (!yandexEnabled && !vkEnabled) return null;

  return (
    <VStack gap={3} align="stretch" width="100%">
      {yandexEnabled ? <YandexSignInButton label={yandexLabel} /> : null}
      {vkEnabled ? <VkSignInButton label={vkLabel} /> : null}
      <Text size="sm" color="secondary">
        Нажимая кнопку, вы соглашаетесь с{" "}
        <Link href={CONSENT_LINKS.privacy} hasUnderline type="inherit">
          обработкой персональных данных
        </Link>
        .
      </Text>
    </VStack>
  );
}

export function OAuthDivider() {
  return (
    <HStack gap={3} vAlign="center" width="100%">
      <div className="h-px min-w-0 flex-1 bg-border" />
      <Text size="sm" color="secondary" className="shrink-0">
        или
      </Text>
      <div className="h-px min-w-0 flex-1 bg-border" />
    </HStack>
  );
}
